import { httpClient } from '../core/http';
import { CrewExecutionRequest, CrewExecutionResponse } from '../types';
import { resolveGenerateCrewId } from '../utils/crew';
import { env } from '../config/env';

export class ExecutingAgentService {
  private basePath = '/v1/executing-agent';
  private readonly generateAgentCrewId = resolveGenerateCrewId({
    devEnvValue: env.REACT_APP_GENERATE_AGENT_CREW_ID_DEV,
    prodEnvValue: env.REACT_APP_GENERATE_AGENT_CREW_ID_PROD,
    devFallback: 32,
  });
  private readonly generateTaskCrewId = resolveGenerateCrewId({
    devEnvValue: env.REACT_APP_GENERATE_TASK_CREW_ID_DEV,
    prodEnvValue: env.REACT_APP_GENERATE_TASK_CREW_ID_PROD,
    devFallback: 33,
  });

  /**
   * Executa IA com streaming via endpoint dedicado.
   * @param payload Payload mínimo para execução
   * @param signal Sinal para cancelamento da requisição
   * @returns Stream de resposta como string
   */
  async executeAiStreamString(
    payload: {
      crew_id: number;
      user_message: string;
      use_canvas: boolean;
      search_context_type?: 'global' | 'specific' | 'web';
      source_ids?: number[];
      connector_source_ids?: number[];
      session_id?: string;
      llm_model?: string;
      attachments?: Array<{
        attachment_id: string;
        file_name: string;
        mime_type: string;
        size_bytes: number;
        width?: number;
        height?: number;
      }>;
    },
    signal?: AbortSignal,
    endpoint: '/v1/execute-ai' | '/v1/execute-ai/live' = '/v1/execute-ai'
  ): Promise<AsyncIterable<string>> {
    const stream = await httpClient.stream(endpoint, payload, {
      signal,
    });
    const reader = stream.getReader();
    const decoder = new TextDecoder();

    return {
      async *[Symbol.asyncIterator]() {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            yield chunk;
          }
        } finally {
          reader.releaseLock();
        }
      },
    };
  }

  async uploadConversationImages(
    files: File[]
  ): Promise<
    Array<{
      attachment_id: string;
      file_name: string;
      mime_type: string;
      size_bytes: number;
      width?: number;
      height?: number;
    }>
  > {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    const response = await httpClient.upload<{
      items: Array<{
        attachment_id: string;
        file_name: string;
        mime_type: string;
        size_bytes: number;
        width?: number;
        height?: number;
      }>;
    }>('/v1/execute-ai/attachments/upload', formData, {
      timeout: 600000,
    });
    return response.data.items || [];
  }

  /**
   * Gera um agente usando o endpoint /v1/execute-ai (stream).
   * @param params Parâmetros para geração do agente
   * @returns Stream de resposta como string
   */
  async generateAgentStream(params: {
    description: string;
    user_message?: string;
    rag_knowledge_source_ids?: number[];
  }): Promise<AsyncIterable<string>> {
    if (!this.generateAgentCrewId) {
      throw new Error('Crew ID de geração de agente não configurado');
    }

    if (!params.description?.trim()) {
      throw new Error('Descrição do agente é obrigatória');
    }

    const userMessage = params.user_message?.trim() || params.description.trim();
    const sessionId = `session_${Date.now()}`;

    return this.executeAiStreamString({
      crew_id: this.generateAgentCrewId,
      user_message: userMessage,
      use_canvas: false,
      session_id: sessionId,
      ...(params.rag_knowledge_source_ids?.length
        ? { source_ids: params.rag_knowledge_source_ids }
        : {}),
    });
  }

  /**
   * Executa crew (endpoint original)
   * @param crewId ID da crew
   * @param inputs Inputs para a crew
   * @returns Resposta da execução
   */
  async executeCrew(
    crewId: number,
    inputs?: Record<string, unknown>
  ): Promise<CrewExecutionResponse> {
    const response = await httpClient.post<CrewExecutionResponse>(
      `${this.basePath}/${crewId}`,
      { inputs } as CrewExecutionRequest,
      {
        timeout: 300000, // 5 minutos de timeout para não cancelar requisições longas
      }
    );
    return response.data;
  }

  /**
   * Executa crew com streaming (endpoint original)
   * @param crewId ID da crew
   * @param inputs Inputs para a crew
   * @returns Stream de resposta
   */
  async executeCrewStreamOriginal(
    crewId: number,
    inputs?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<ReadableStream> {
    return httpClient.stream(
      `${this.basePath}/${crewId}/stream`,
      {
        inputs,
      } as CrewExecutionRequest,
      { signal }
    );
  }

  /**
   * Executa crew com streaming sem RAG (novo formato)
   * @param crewId ID da crew
   * @param payload Payload da requisição
   * @returns Stream de resposta
   */
  async executeCrewStreamWithoutRag(
    crewId: number,
    payload: {
      inputs: {
        user_message: string;
        session_id: string;
        organization_id: number;
        crew_id: number;
        use_canvas?: boolean;
      };
    },
    signal?: AbortSignal
  ): Promise<ReadableStream> {
    return httpClient.stream(`${this.basePath}/${crewId}/stream`, payload, {
      signal,
    });
  }

  /**
   * Executa crew com RAG e streaming (endpoint original)
   * @param crewId ID da crew
   * @param ragRequest Configuração RAG
   * @returns Stream de resposta
   */
  async executeCrewRagStreamOriginal(
    crewId: number,
    ragRequest: {
      inputs: {
        user_message: string;
        session_id: string;
        organization_id: number;
        crew_id: number;
        use_canvas?: boolean;
      };
      use_rag: boolean;
      rag_query: string;
      organization_id: number;
      rag_tags: string[];
      rag_knowledge_source_ids: number[];
    },
    signal?: AbortSignal
  ): Promise<ReadableStream> {
    return httpClient.stream(
      `${this.basePath}/${crewId}/rag/stream`,
      ragRequest,
      { signal }
    );
  }

  /**
   * Executa crew com streaming (endpoint correto da collection)
   * @param conversationId ID da conversa
   * @param message Mensagem para enviar
   * @param crewId ID da crew
   * @returns Stream de resposta
   */
  async executeCrewStream(
    conversationId: number,
    message: string,
    crewId: number,
    signal?: AbortSignal
  ): Promise<ReadableStream> {
    const body = {
      message,
      crew_id: crewId,
    };

    return httpClient.stream(
      `${this.basePath}/conversation/${conversationId}/stream`,
      body,
      { signal }
    );
  }

  /**
   * Executa crew com RAG e streaming (endpoint correto da collection)
   * @param conversationId ID da conversa
   * @param message Mensagem para enviar
   * @param crewId ID da crew
   * @returns Stream de resposta
   */
  async executeCrewRagStream(
    conversationId: number,
    message: string,
    crewId: number,
    signal?: AbortSignal
  ): Promise<ReadableStream> {
    const body = {
      message,
      crew_id: crewId,
    };

    return httpClient.stream(
      `${this.basePath}/conversation/${conversationId}/rag/stream`,
      body,
      { signal }
    );
  }

  /**
   * Obtém interações do agente (endpoint correto da collection)
   * @param conversationId ID da conversa
   * @returns Interações do agente
   */
  async getAgentInteractions(conversationId: number): Promise<unknown> {
    const response = await httpClient.get(
      `${this.basePath}/interactions/${conversationId}`
    );
    return response.data;
  }

  /**
   * Executa crew com streaming (versão string - endpoint original)
   * @param crewId ID da crew
   * @param inputs Inputs para a crew
   * @returns Stream de resposta como string
   */
  async executeCrewStreamStringOriginal(
    crewId: number,
    inputs?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<AsyncIterable<string>> {
    const stream = await this.executeCrewStreamOriginal(crewId, inputs, signal);
    const reader = stream.getReader();
    const decoder = new TextDecoder();

    return {
      async *[Symbol.asyncIterator]() {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            yield chunk;
          }
        } finally {
          reader.releaseLock();
        }
      },
    };
  }

  /**
   * Executa crew com streaming sem RAG (versão string - novo formato)
   * @param crewId ID da crew
   * @param payload Payload da requisição
   * @returns Stream de resposta como string
   */
  async executeCrewStreamStringWithoutRag(
    crewId: number,
    payload: {
      inputs: {
        user_message: string;
        session_id: string;
        organization_id: number;
        crew_id: number;
        use_canvas?: boolean;
      };
    },
    signal?: AbortSignal
  ): Promise<AsyncIterable<string>> {
    const stream = await this.executeCrewStreamWithoutRag(
      crewId,
      payload,
      signal
    );
    const reader = stream.getReader();
    const decoder = new TextDecoder();

    return {
      async *[Symbol.asyncIterator]() {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            yield chunk;
          }
        } finally {
          reader.releaseLock();
        }
      },
    };
  }

  /**
   * Executa crew com RAG e streaming (versão string - endpoint original)
   * @param crewId ID da crew
   * @param ragRequest Configuração RAG
   * @returns Stream de resposta como string
   */
  async executeCrewRagStreamStringOriginal(
    crewId: number,
    ragRequest: {
      inputs: {
        user_message: string;
        session_id: string;
        organization_id: number;
        crew_id: number;
        use_canvas?: boolean;
      };
      use_rag: boolean;
      rag_query: string;
      organization_id: number;
      rag_tags: string[];
      rag_knowledge_source_ids: number[];
    },
    signal?: AbortSignal
  ): Promise<AsyncIterable<string>> {
    const stream = await this.executeCrewRagStreamOriginal(
      crewId,
      ragRequest,
      signal
    );
    const reader = stream.getReader();
    const decoder = new TextDecoder();

    return {
      async *[Symbol.asyncIterator]() {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            yield chunk;
          }
        } finally {
          reader.releaseLock();
        }
      },
    };
  }

  /**
   * Executa crew com streaming (versão string - endpoint correto da collection)
   * @param conversationId ID da conversa
   * @param message Mensagem para enviar
   * @param crewId ID da crew
   * @returns Stream de resposta como string
   */
  async executeCrewStreamString(
    conversationId: number,
    message: string,
    crewId: number,
    signal?: AbortSignal
  ): Promise<AsyncIterable<string>> {
    const stream = await this.executeCrewStream(
      conversationId,
      message,
      crewId,
      signal
    );
    const reader = stream.getReader();
    const decoder = new TextDecoder();

    return {
      async *[Symbol.asyncIterator]() {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            yield chunk;
          }
        } finally {
          reader.releaseLock();
        }
      },
    };
  }

  /**
   * Executa crew com RAG e streaming (versão string - endpoint correto da collection)
   * @param conversationId ID da conversa
   * @param message Mensagem para enviar
   * @param crewId ID da crew
   * @returns Stream de resposta como string
   */
  async executeCrewRagStreamString(
    conversationId: number,
    message: string,
    crewId: number,
    signal?: AbortSignal
  ): Promise<AsyncIterable<string>> {
    const stream = await this.executeCrewRagStream(
      conversationId,
      message,
      crewId,
      signal
    );
    const reader = stream.getReader();
    const decoder = new TextDecoder();

    return {
      async *[Symbol.asyncIterator]() {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            yield chunk;
          }
        } finally {
          reader.releaseLock();
        }
      },
    };
  }

  /**
   * Executa conversa com streaming e gerenciamento de sessão (método original)
   * @param crewId ID da crew
   * @param message Mensagem para enviar
   * @param sessionId ID da sessão
   * @param ragConfig Configuração RAG
   * @param useCanvas Se deve usar canvas para geração de relatório
   * @returns Stream de resposta e função para obter session ID
   */
  async executeConversationStreamWithSession(
    crewId: number,
    message: string,
    sessionId?: string,
    ragConfig?: {
      use_rag: boolean;
      rag_query: string;
      organization_id: number;
      rag_top_k: number;
      rag_similarity_threshold: number;
      rag_tags: string[];
      rag_knowledge_source_ids: number[];
      rag_max_context_length: number;
    },
    useCanvas: boolean = false,
    signal?: AbortSignal
  ): Promise<{ stream: AsyncIterable<string>; getSessionId: () => string }> {
    let stream: AsyncIterable<string>;
    const currentSessionId = sessionId || `session_${Date.now()}`;

    if (ragConfig?.use_rag && ragConfig.rag_knowledge_source_ids.length > 0) {
      // Com RAG - usar o endpoint RAG
      stream = await this.executeCrewRagStreamStringOriginal(crewId, {
        inputs: {
          user_message: message,
          session_id: currentSessionId,
          organization_id: ragConfig.organization_id,
          crew_id: crewId,
          use_canvas: useCanvas,
        },
        use_rag: true,
        rag_query: ragConfig.rag_query,
        organization_id: ragConfig.organization_id,
        rag_tags: ragConfig.rag_tags,
        rag_knowledge_source_ids: ragConfig.rag_knowledge_source_ids,
      }, signal);
    } else {
      // Sem RAG - usar o endpoint simples
      stream = await this.executeCrewStreamStringWithoutRag(
        crewId,
        {
          inputs: {
            user_message: message,
            session_id: currentSessionId,
            organization_id: ragConfig?.organization_id || 1,
            crew_id: crewId,
            use_canvas: useCanvas,
          },
        },
        signal
      );
    }

    return {
      stream,
      getSessionId: () => currentSessionId,
    };
  }

  /**
   * Gera um agente usando o endpoint direto
   * @param params Parâmetros para geração do agente
   * @returns Resposta da geração
   */
  async generateAgent(params: {
    description: string;
    organization_id: number;
    rag_knowledge_source_ids?: number[];
    use_rag?: boolean;
    user_message?: string;
  }): Promise<CrewExecutionResponse> {
    const payload: {
      description: string;
      organization_id: number;
      inputs: {
        user_message: string;
      };
      rag_knowledge_source_ids?: number[];
      use_rag?: boolean;
    } = {
      description: params.description,
      organization_id: params.organization_id,
      inputs: {
        user_message: params.user_message || params.description,
      },
    };

    if (
      params.rag_knowledge_source_ids &&
      params.rag_knowledge_source_ids.length > 0
    ) {
      payload.rag_knowledge_source_ids = params.rag_knowledge_source_ids;
      payload.use_rag = params.use_rag ?? true;
    }

    const response = await httpClient.post<CrewExecutionResponse>(
      '/v1/generate-agent',
      payload,
      {
        timeout: 300000, // 5 minutos de timeout
      }
    );
    return response.data;
  }

  /**
   * Gera uma task usando o endpoint direto
   * @param params Parâmetros para geração da task
   * @returns Resposta da geração
   */
  async generateTask(params: {
    description: string;
    agent_context: string;
    format_expected?: string;
    use_rag?: boolean;
    organization_id: number;
    rag_knowledge_source_ids?: number[];
    user_message?: string;
  }): Promise<CrewExecutionResponse> {
    const payload: {
      description: string;
      agent_context: string;
      use_rag: boolean;
      organization_id: number;
      inputs: {
        user_message: string;
      };
      format_expected?: string;
      rag_knowledge_source_ids?: number[];
    } = {
      description: params.description,
      agent_context: params.agent_context,
      use_rag: params.use_rag ?? false,
      organization_id: params.organization_id,
      inputs: {
        user_message: params.user_message || params.description,
      },
    };

    if (params.format_expected) {
      payload.format_expected = params.format_expected;
    }

    if (params.rag_knowledge_source_ids && params.rag_knowledge_source_ids.length > 0) {
      payload.rag_knowledge_source_ids = params.rag_knowledge_source_ids;
    }

    const response = await httpClient.post<CrewExecutionResponse>(
      '/v1/generate-task',
      payload,
      {
        timeout: 300000, // 5 minutos de timeout
      }
    );
    return response.data;
  }

  /**
   * Gera uma task usando o endpoint /v1/execute-ai (stream).
   * @param params Parâmetros para geração da task
   * @returns Stream de resposta como string
   */
  async generateTaskStream(params: {
    description: string;
    agent_context: string;
    format_expected?: string;
    user_message?: string;
    rag_knowledge_source_ids?: number[];
  }): Promise<AsyncIterable<string>> {
    if (!this.generateTaskCrewId) {
      throw new Error('Crew ID de geração de task não configurado');
    }

    if (!params.description?.trim()) {
      throw new Error('Descrição da task é obrigatória');
    }

    if (!params.agent_context?.trim()) {
      throw new Error('Contexto do agente é obrigatório');
    }

    const baseMessage = params.user_message?.trim() || params.description.trim();
    const agentContextMessage = `\n\nContexto_do_agente:\n${params.agent_context}`;
    const formatExpectedMessage = params.format_expected
      ? `\n\nFormato_esperado:\n${params.format_expected}`
      : '';
    const userMessage = `${baseMessage}${agentContextMessage}${formatExpectedMessage}`;
    const sessionId = `session_${Date.now()}`;

    return this.executeAiStreamString({
      crew_id: this.generateTaskCrewId,
      user_message: userMessage,
      use_canvas: false,
      session_id: sessionId,
      ...(params.rag_knowledge_source_ids?.length
        ? { source_ids: params.rag_knowledge_source_ids }
        : {}),
    });
  }
}

export const executingAgentService = new ExecutingAgentService();
